// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';

// material ui
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { daDK } from '@mui/x-data-grid/locales';

// api
import type { GeneralQoSRecord } from '@/api/generated/serviceSystemMonitoring/endpoints/serviceSystemMonitoring';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

type QualityOfServiceRow = GeneralQoSRecord & { id: string };

type QualityOfServiceDataGridProps = {
  rows: QualityOfServiceRow[];
};

const QualityOfServiceDataGrid = ({ rows }: QualityOfServiceDataGridProps) => {
  const { t } = useTranslation('qualityOfService');
  const dispatch = useDispatch();
  const handleCopyId = async (unit: QualityOfServiceRow) => {
    if (!unit.unitId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(unit.unitId));
      dispatch(
        showSnackbar({
          message: t('units.unitIdCopied', {
            id: unit.unitId,
            ns: 'units-gateways-sensors',
          }),
          severity: 'success',
        }),
      );
    } catch (error) {
      console.error('Copy unit id failed:', error);
      dispatch(
        showSnackbar({
          message: t('units.unitIdCopyFailed', {
            ns: 'units-gateways-sensors',
          }),
          severity: 'error',
        }),
      );
    }
  };
  const columns: GridColDef<QualityOfServiceRow>[] = [
    {
      field: 'unitName',
      headerName: t('columns.unitName', { defaultValue: 'Unit Name' }),
      flex: 2,
      renderCell: (params) => (
        <Link to={`/$accountId/units/${params.row.unitId}`}>
          {params.row.unitName}
        </Link>
      ),
    },
    {
      field: 'unitId',
      headerName: t('columns.unitId', { defaultValue: 'Unit ID' }),
      flex: 1,
      renderCell: (params: GridRenderCellParams<QualityOfServiceRow>) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            height: '100%',
            width: '100%',
            '& .copy-button': { opacity: 0, transition: 'opacity 0.2s ease' },
            '&:hover .copy-button': { opacity: 1 },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value ?? t('common.notAvailable')}
          </Typography>
          {params.value != null && (
            <IconButton
              className="copy-button"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                console.log('params.row.raw', params.row);
                handleCopyId(params.row);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ),
    },
    {
      field: 'unitTypeInputId',
      headerName: t('columns.unitTypeInputId', {
        defaultValue: 'Unit Type Input ID',
      }),
      flex: 1,
    },
    {
      field: 'dataType',
      headerName: t('columns.dataType', { defaultValue: 'Data Type' }),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'aggregateType',
      headerName: t('columns.aggregateType', {
        defaultValue: 'Chosen Aggregate',
      }),
      flex: 1,
    },
    {
      field: 'window',
      headerName: t('columns.window', { defaultValue: 'Operation Window' }),
      flex: 1,
      type: 'number',
    },
    {
      field: 'offset',
      headerName: t('columns.offset', { defaultValue: 'Latency' }),
      flex: 1,
      type: 'number',
    },
    {
      field: 'threshold',
      headerName: t('columns.threshold', { defaultValue: 'Threshold' }),
      flex: 1,
      type: 'number',
      valueFormatter: (value) => `${(Number(value) * 100).toFixed(0)}%`,
    },
    {
      field: 'dataRatio',
      headerName: t('columns.dataRatio', { defaultValue: 'Data Ratio' }),
      flex: 1,
      type: 'number',
      valueFormatter: (value) => `${(Number(value) * 100).toFixed(0)}%`,
    },
    {
      field: 'aboveThreshold',
      headerName: t('columns.status', { defaultValue: 'Status' }),
      flex: 1,
      valueGetter: (_, row) =>
        row.aboveThreshold
          ? t('status.aboveThreshold', { defaultValue: 'Above Threshold' })
          : t('status.belowThreshold', { defaultValue: 'Below Threshold' }),
      renderCell: (params) => {
        const isAboveThreshold = params.row.aboveThreshold;
        return (
          <Chip
            size="small"
            label={
              isAboveThreshold
                ? t('status.aboveThreshold', {
                    defaultValue: 'Above Threshold',
                  })
                : t('status.belowThreshold', {
                    defaultValue: 'Below Threshold',
                  })
            }
            color={isAboveThreshold ? 'success' : 'error'}
            variant="outlined"
            sx={{
              backgroundColor: isAboveThreshold
                ? 'rgba(0, 128, 0, 0.1)'
                : 'rgba(255, 0, 0, 0.1)',
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: t('columns.actions', { defaultValue: 'Actions' }),
      flex: 0.5,
      renderCell: (params) => {
        const alarmConfigurationId = Number(
          params.row.alarmConfigId.split('_')[0],
        );
        console.log(params.row);
        if (Number.isNaN(alarmConfigurationId)) {
          return null;
        }

        return (
          <IconButton
            component={Link}
            to={`/$accountId/alarms/configuration/${alarmConfigurationId}`}
            sx={{ borderRadius: 2 }}
          >
            <SettingsRoundedIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <DataGridPremium
      rows={rows}
      columns={columns}
      localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
      showToolbar
      disableRowSelectionOnClick
      slots={{ toolbar: DataGridToolbar }}
    />
  );
};

export default QualityOfServiceDataGrid;
